import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge as UiBadge } from '@/components/ui/badge';
import { toast } from 'react-hot-toast';
import { Loader2, PlusCircle, Edit, Trash2, Eye, CheckCircle, XCircle, HelpCircle, UserPlus, Users } from 'lucide-react';
import MissionForm from './MissionForm';
import AssignMissionModal from './AssignMissionModal';
import AssignMissionToAllModal from './AssignMissionToAllModal';
import { listMissionTemplates, deleteMissionTemplate } from '@/lib/gamification/missionService';
import { Mission, MissionRow } from '@/lib/gamification/types';
import { supabase } from '@/lib/supabase';
import { Json } from '../../../types/supabase';
import { useTheme } from '@/contexts/ThemeContext';

interface MissionsAdminProps {
  onOpenHelp: () => void;
}

const MissionsAdmin: React.FC<MissionsAdminProps> = ({ onOpenHelp }) => {
  const { theme } = useTheme();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingMission, setEditingMission] = useState<MissionRow | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para o modal de atribuição individual
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [missionToAssign, setMissionToAssign] = useState<Mission | null>(null);

  // Estado para o modal de atribuição a todos
  const [showAssignToAllModal, setShowAssignToAllModal] = useState(false);
  const [missionToAssignToAll, setMissionToAssignToAll] = useState<Mission | null>(null);

  const fetchMissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listMissionTemplates(supabase);
      setMissions(data);
    } catch (err: any) {
      setError('Falha ao carregar modelos de missão.');
      toast.error('Falha ao carregar modelos de missão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  const handleDelete = async (missionId: string) => {
    toast((t) => (
      <div className={`flex flex-col items-center p-2 rounded-md shadow-lg ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-800'}`}>
        <p className="font-semibold text-center mb-2">Confirmar exclusão?</p>
        <p className={`text-sm mb-3 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Esta ação não pode ser desfeita.</p>
        <div className="flex w-full gap-2">
          <Button
            variant="destructive"
            size="sm"
            className="flex-1"
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const result = await deleteMissionTemplate(supabase, missionId);
                if (result.success) {
                  toast.success('Missão excluída!');
                  fetchMissions(); 
                } else {
                  throw result.error || new Error('Falha ao excluir missão');
                }
              } catch (error: any) {
                toast.error(error.message || 'Falha ao excluir missão.');
                console.error("Erro ao excluir missão:", error);
              }
            }}
          >
            <Trash2 className="mr-2 h-4"/> Excluir
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.dismiss(t.id)}>
            Cancelar
          </Button>
        </div>
      </div>
    ), { duration: 6000 });
  };

  const handleSaveMission = (savedMission: Mission) => {
    setShowForm(false);
    setEditingMission(null);
    fetchMissions(); 
    // O toast de sucesso já é mostrado pelo MissionForm
  };
  
  const handleEdit = (mission: Mission) => {
    const missionRowData: MissionRow = {
      ...mission,
      required_actions: mission.required_actions as unknown as Json, // Cast para Json
    };
    setEditingMission(missionRowData);
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingMission(null);
    setShowForm(true);
  };

  if (showForm) {
    return (
      <MissionForm 
        initialData={editingMission}
        onSave={handleSaveMission}
        onCancel={() => { setShowForm(false); setEditingMission(null); }}
        isLoading={isSubmitting} // Passar estado de submitting para o form
      />
    );
  }

  return (
    <>
      <Card className={theme === 'dark' ? 'dark' : ''}>
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle>Gerenciar Modelos de Missão</CardTitle>
            <CardDescription>Crie, edite ou remova modelos de missão para os usuários.</CardDescription>
          </div>
          <div className="flex items-center space-x-2">
            <Button onClick={handleCreateNew}>
              <PlusCircle className="mr-2 h-4 w-4" /> Criar Nova Missão
            </Button>
            <Button variant="outline" size="icon" onClick={onOpenHelp} title="Ajuda Configuração JSON">
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center items-center py-10">
              <Loader2 className={`h-8 w-8 animate-spin ${theme === 'dark' ? 'text-indigo-400' : 'text-primary'}`} />
            </div>
          )}
          {error && <p className={`text-center py-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-500'}`}>{error}</p>}
          {!loading && !error && missions.length === 0 && <p className={`text-center py-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Nenhum modelo de missão cadastrado.</p>}
          {!loading && !error && missions.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Chave</TableHead>
                    <TableHead>Título</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Frequência</TableHead>
                    <TableHead>XP</TableHead>
                    <TableHead>Badge</TableHead>
                    <TableHead>Ativa</TableHead>
                    <TableHead>Pública?</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {missions.map((mission) => (
                    <TableRow key={mission.id}>
                      <TableCell className={`font-mono text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{mission.mission_key}</TableCell>
                      <TableCell className="font-medium">{mission.title}</TableCell>
                      <TableCell>{mission.mission_type}</TableCell>
                      <TableCell>{mission.frequency}</TableCell>
                      <TableCell>{mission.xp_reward}</TableCell>
                      <TableCell>{mission.badge_reward_id ? <Eye className={`h-4 w-4 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}/> : '-'}</TableCell>
                      <TableCell>
                        {mission.is_active ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500"/>}
                      </TableCell>
                      <TableCell>
                        {mission.is_publicly_assignable ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-red-500"/>}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="icon" onClick={() => handleEdit(mission)} title="Editar Missão">
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Editar</span>
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          onClick={() => {
                            setMissionToAssign(mission);
                            setShowAssignModal(true);
                          }}
                          title="Atribuir Missão a Usuários Específicos"
                          disabled={!mission.is_active} // Só permite atribuir missões ativas
                        >
                          <UserPlus className="h-4 w-4" />
                          <span className="sr-only">Atribuir a Usuários</span>
                        </Button>
                        <Button 
                          variant="outline" 
                          size="icon" 
                          onClick={() => {
                            setMissionToAssignToAll(mission);
                            setShowAssignToAllModal(true);
                          }}
                          title="Atribuir Missão a Todos os Usuários"
                          disabled={!mission.is_active} // Só permite atribuir missões ativas
                        >
                          <Users className="h-4 w-4" />
                          <span className="sr-only">Atribuir a Todos</span>
                        </Button>
                        <Button variant="destructive" size="icon" onClick={() => handleDelete(mission.id)} title="Excluir Missão">
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Excluir</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Renderiza o Modal de Atribuição Individual */}
      {missionToAssign && (
        <AssignMissionModal 
          mission={missionToAssign} 
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setMissionToAssign(null);
          }}
        />
      )}

      {missionToAssignToAll && (
        <AssignMissionToAllModal 
          mission={missionToAssignToAll} 
          isOpen={showAssignToAllModal}
          onClose={() => {
            setShowAssignToAllModal(false);
            setMissionToAssignToAll(null);
          }}
          // onAssignedToAll={(success, failed) => console.log(`Assigned to all - Success: ${success}, Failed: ${failed}`)}
        />
      )}
    </>
  );
};

export default MissionsAdmin; 